/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib — audits a record's loaded relationships against the
 *              `total_<relation>_number` counters that travel in the same payload
 *   key:       auditRelationshipCounters(record, counters), EVENT_RELATION_COUNTERS,
 *              readLifecycle(record), readCanonicalRelationshipIds(store, record, relation),
 *              hasLocalRelationshipEdits(store, record)
 *   uses:      -
 *   siblings:  controllers/application.js (saveRecordRelated's pre-flight),
 *              lib/event-participants-guard.js (the other half of that pre-flight),
 *              beebee-base-model/lib/utility.js (_saveAll, which turns the loaded
 *              relationships into a full-replacement payload)
 *   tests:     relationship-integrity-test.js, hasmany-reference-semantics-test.js
 *   gotchas:   a brand-new record carries counters of 0 while its relationships
 *              are already filled — the audit is only meaningful on a persisted
 *              record, and the caller decides that. `linked` and `remote` are
 *              diagnostic ONLY; `removed` is not — it is the one column that
 *              changes the verdict, because a relation the user emptied on
 *              purpose reads exactly like one the client lost.
 *              On ember-data 5.8.2 `hasMany(rel).ids()` and `cache.getRelationship()`
 *              both report LOCAL state (measured); only
 *              `cache.changedRelationships()` keeps the remote side, which is
 *              what readCanonicalRelationshipIds reads.
 *
 * WHY THIS EXISTS
 * A save of an appointment sends every relationship as a complete list, and
 * `update_event_all_v1` deletes the existing links before re-inserting exactly
 * what arrived. So a client that has lost its relationship cache does not save a
 * partial appointment: it erases one. It happened in production (customer
 * `nicolagualla`, 2026-08-17, event 19741): create → save → reopen → edit →
 * save, and the second save went out with `contacts`, `products`, `custom_fields`
 * and `addresses` all empty while the attributes were intact.
 *
 * That payload contradicted itself. Every `total_<relation>_number` attribute
 * arrives in the same payload as the relationship it counts, so the client can
 * check its own state before writing: the failing save carried
 * `total_contacts_number: 1` next to `contacts: []`. This module reports that
 * contradiction; what to do about it is the caller's call.
 *
 * WHY `linked` IS THERE TOO
 * The API was cleared on 2026-08-25: queried live as the affected user, both the
 * detail and the list endpoints answer complete and consistent, and the SQL
 * agrees (the products counter and the products linkage share one predicate). So
 * the contradiction is built client-side, and the open question is *where*:
 * whether the record still holds its linkage and only the materialisation comes
 * back empty (a torn-down or swapped context — the ember-data 5.8
 * "this.promise is null" family), or whether the linkage itself was wiped.
 * `linked` answers exactly that: it reads the ids through the relationship
 * reference, which needs no resolution and no fetch. (It reads the LOCAL side —
 * see readLinkedIds; an earlier version of this note called it canonical, which
 * measurement disproved.)
 *
 * WHAT THIS MUST NOT DO
 * Emptying a relation ON PURPOSE is ordinary work: taking the only treatment off
 * an appointment leaves `products` empty next to a `total_products_number` of 1,
 * which is bit-for-bit the contradiction this module reports. Keying the verdict
 * on the counter alone therefore refused the removal and told the user to reload
 * — which puts the treatment back (reproduced end-to-end, 2026-08-27; in
 * production since 1.3.188). The user's intent is recorded where the user acts:
 * `delRelationship` writes the removed record into `removedRelated`, and nothing
 * else does — client code that clears an array (the `usaPacchettoTask` class of
 * bug) leaves no such trace. So a relation is only excused when EVERY id the
 * server had for it is accounted for there; one unexplained id and the save is
 * still refused. `remote` alone could not do this: a deliberate removal and a
 * cleared array are both local edits, and both read `linked 0 · remote N`.
 */

// Relations of an `event` that the server counts for us. Only these can be
// audited — `custom_fields` and `addresses` have no counter on the model, so a
// lost cache is invisible there.
const EVENT_RELATION_COUNTERS = Object.freeze({
  contacts: 'total_contacts_number',
  products: 'total_products_number',
  projects: 'total_projects_number',
  payments: 'total_payments_number',
  workhours: 'total_workhours_number',
  reminders: 'total_reminders_number',
  related_events: 'total_related_events_number'
});
function readCounter(record, counterKey) {
  const raw = record?.[counterKey];
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  const value = Number(raw);
  return Number.isNaN(value) ? null : value;
}
async function readLength(record, relation) {
  try {
    // Reading through `await` resolves an async hasMany and passes a sync one
    // straight through. A relation the model does not declare reads as
    // undefined, which is not evidence of anything.
    const resolved = await record?.[relation];
    if (resolved === null || resolved === undefined) {
      return null;
    }
    return typeof resolved.length === 'number' ? resolved.length : null;
  } catch (e) {
    // An unreadable relation (rejected fetch, torn-down record) says nothing
    // about data loss: report it as unknown, never as empty.
    return null;
  }
}

/**
 * How many ids the relationship still points at, read through the reference,
 * with no resolution and no fetch.
 *
 * NOT the canonical state — this comment used to claim it was, and it is wrong:
 * measured on ember-data 5.8.2, `hasMany(relation).ids()` reports the LOCAL
 * state, local edits included (hasmany-reference-semantics-test.js pins it).
 * So `linked` answers "how many does the record point at right now", which next
 * to `loaded` tells a lost cache from a lost materialisation. For the canonical
 * side use `readCanonicalRelationshipIds` below.
 *
 * A relation the model does not declare throws inside `hasMany()`, which is
 * unknown, not empty.
 */
function readLinkedIds(record, relation) {
  try {
    const ids = record?.hasMany?.(relation)?.ids?.();
    return Array.isArray(ids) ? ids.filter(id => id !== null && id !== undefined).length : null;
  } catch (e) {
    return null;
  }
}

/**
 * The ids the SERVER last told this client the relationship holds — the remote
 * side, before any edit the user made in this session.
 *
 * This is the only way to tell a participant the user deliberately removed from
 * one the read never showed: the removed one is in here, the hidden one never
 * was. Neither `hasMany(relation).ids()` nor `cache.getRelationship()` can
 * answer it — both report local state on ember-data 5.8.2 (measured, see
 * hasmany-reference-semantics-test.js); `cache.changedRelationships()` keeps the
 * two sides apart, so that is what this reads.
 *
 * @param {Object} store - the ember-data store service
 * @param {Object} record - an ember-data record
 * @param {string} relation - relationship name, e.g. 'contacts'
 * @returns {Array<string>|null} remote ids as strings, or null when there is no
 *          answer to give: no local change (local IS the remote state then), an
 *          undeclared relation, or an unreadable cache. Never [] for "unknown" —
 *          a caller must not read "the server linked nobody" out of an error.
 */
function readCanonicalRelationshipIds(store, record, relation) {
  const entries = readCanonicalRelationshipEntries(store, record, relation);
  return entries === null ? null : entries.map(entry => entry.id);
}

/**
 * The same remote side, as identifier entries: `{ type, id }`. The model each id
 * belongs to is what lets a removal be matched against `removedRelated`, whose
 * bookkeeping is keyed by `constructor.modelName` and id — two relations can
 * carry the same numeric id, so the ids alone would match across models.
 *
 * @param {Object} store - the ember-data store service
 * @param {Object} record - an ember-data record
 * @param {string} relation - relationship name, e.g. 'products'
 * @returns {Array<{type: string|null, id: string}>|null} null on the same
 *          "no answer to give" cases as readCanonicalRelationshipIds.
 */
function readCanonicalRelationshipEntries(store, record, relation) {
  try {
    const modelName = record?.constructor?.modelName;
    if (!store || !modelName || !record?.id) {
      return null;
    }
    const identifier = store.identifierCache?.getOrCreateRecordIdentifier?.({
      type: modelName,
      id: record.id
    });
    if (!identifier) {
      return null;
    }
    const changed = store.cache?.changedRelationships?.(identifier);
    // A Map keyed by field name; a relationship with no local edit is absent.
    const remote = changed?.get?.(relation)?.remoteState;
    if (!Array.isArray(remote)) {
      return null;
    }
    return remote.filter(entry => entry?.id !== null && entry?.id !== undefined).map(entry => ({
      type: entry.type ?? null,
      id: String(entry.id)
    }));
  } catch (e) {
    return null;
  }
}

/**
 * What the user took off this record ON PURPOSE, as `type:id` keys.
 *
 * `delRelationship` (beebee-base/lib/utility/relationships) pushes the removed
 * record into `removedRelated` and `addRelationship` takes it back out, so the
 * field holds exactly what the user meant to unlink and nothing else. Client
 * code that clears a relationship array does not go through either helper, which
 * is precisely why this can tell the two apart.
 *
 * @param {Object} record - an ember-data record
 * @returns {Set<string>} `modelName:id` keys; empty when the record carries no
 *          removals, or when the field cannot be read — an unreadable field
 *          proves no removal, so nothing gets excused on its account.
 */
function readDeliberateRemovals(record) {
  const keys = new Set();
  try {
    const removed = record?.removedRelated;
    if (!Array.isArray(removed)) {
      return keys;
    }
    for (const item of removed) {
      const type = item?.constructor?.modelName;
      const id = item?.id;
      if (!type || id === null || id === undefined) continue;
      keys.add(`${type}:${id}`);
    }
  } catch (e) {
    return keys;
  }
  return keys;
}

/**
 * Was this relation emptied by the user rather than lost by the client? Only
 * when the server's whole list for it is accounted for in `removedRelated`: one
 * id left unexplained and the save still writes a deletion nobody asked for.
 *
 * @param {{remote: number|null, removed: number|null}} row - one audit row
 * @returns {boolean}
 */
function isDeliberatelyEmptied(row) {
  return row.remote > 0 && row.removed === row.remote;
}

/**
 * Does this record carry ANY local relationship edit the user has not saved yet?
 *
 * Asked before a reload, never after: a reload restores the server's side of
 * every relationship, so it can only ADD back what the client lost — unless the
 * client also holds an edit of its own, in which case the reload silently
 * discards the user's work and a retry would then write the resurrected state
 * back to the server as if nobody had touched it. One caller
 * (`recoverStaleEventSaveTask`), one purpose: decide whether a reload is safe.
 *
 * Two sources, because the two record different things: `addedRelated` /
 * `removedRelated` are the bookkeeping `addRelationship`/`delRelationship` keep
 * for the save payload, and `cache.changedRelationships()` is ember-data's own
 * record of every relationship whose local side diverged from the remote one —
 * including the relations with no counter (`custom_fields`, `addresses`) that
 * the audit cannot see.
 *
 * FAIL-CLOSED, unlike everything else in this module: "cannot tell" answers
 * true. A guard that cannot read the cache must not conclude a reload is
 * harmless; the cost of being wrong here is the user's unsaved edit.
 *
 * @param {Object} store - the ember-data store service
 * @param {Object} record - an ember-data record
 * @returns {boolean} true when the record has (or might have) local edits
 */
function hasLocalRelationshipEdits(store, record) {
  try {
    const added = record?.addedRelated;
    const removed = record?.removedRelated;
    if (Array.isArray(added) && added.length) return true;
    if (Array.isArray(removed) && removed.length) return true;
    const modelName = record?.constructor?.modelName;
    if (!store || !modelName || !record?.id) return true;
    const identifier = store.identifierCache?.getOrCreateRecordIdentifier?.({
      type: modelName,
      id: record.id
    });
    if (!identifier) return true;
    const changed = store.cache?.changedRelationships?.(identifier);
    // A Map keyed by field name; a relationship with no local edit is absent.
    if (!changed || typeof changed.size !== 'number') return true;
    return changed.size > 0;
  } catch (e) {
    return true;
  }
}

/**
 * The record's own state at this instant. A save running against a torn-down or
 * swapped context is the leading explanation for an empty cache, and this is how
 * a refusal reports it.
 *
 * @param {Object} record
 * @returns {{isNew: boolean, isDestroying: boolean, isDestroyed: boolean, state: string|null}}
 */
function readLifecycle(record) {
  return {
    isNew: Boolean(record?.isNew),
    isDestroying: Boolean(record?.isDestroying),
    isDestroyed: Boolean(record?.isDestroyed),
    state: record?.currentState?.stateName ?? null
  };
}

/**
 * Compare each relation's loaded length with the counter the server sent for it,
 * and record how many ids the relationship still points at.
 *
 * @param {Object} record - an Ember Data record
 * @param {Object} [counters] - { relation: counterAttribute }
 * @param {Object} [store] - the ember-data store, when the caller has one. Only
 *        needed for `remote`; without it that column reads null.
 * @returns {Promise<{audit: Array<{relation: string, loaded: number|null, counter: number|null, linked: number|null, remote: number|null, removed: number|null}>,
 *                    mismatches: Array<{relation: string, loaded: number, counter: number, linked: number|null, remote: number|null, removed: number|null}>,
 *                    lifecycle: {isNew: boolean, isDestroying: boolean, isDestroyed: boolean, state: string|null}}>}
 *          `mismatches` holds only the contradictions worth refusing: the server
 *          counted something, the client holds nothing, and nobody asked for it
 *          to go. `linked` and `remote` do not change that verdict — they say
 *          WHERE the loss happened — while `removed` does, because it is the
 *          only column that carries the user's intent:
 *
 *            loaded 0 · linked N            → linkage intact, materialisation
 *                                             failed (torn-down / swapped
 *                                             context). NEVER OBSERVED: over the
 *                                             60 refusals Sentry kept for
 *                                             EMBER-MEDICAL-2WW, `linked` was 0
 *                                             in every single mismatch and the
 *                                             record was `root.loaded.updated.
 *                                             uncommitted`, never destroying.
 *                                             The hypothesis is withdrawn
 *                                             (POP-154 §12.1); the row stays
 *                                             only to say it was looked for.
 *            loaded 0 · linked 0 · remote N · removed N → the user unlinked them
 *            loaded 0 · linked 0 · remote N · removed <N → client code cleared it
 *            loaded 0 · linked 0 · remote —  → a push overwrote the linkage
 *                                             (48 of those 60; measured cause of
 *                                             the empty remote side: an explicit
 *                                             `data: []` in a read — see
 *                                             lib/payload-relationships.js)
 *
 *          `remote` reads null whenever the relationship carries no local edit,
 *          because then local IS the remote state and there is nothing to split;
 *          `removed` reads null wherever `remote` does, having nothing to count
 *          against.
 */
async function auditRelationshipCounters(record, counters = EVENT_RELATION_COUNTERS, store = null) {
  const audit = [];
  const removals = readDeliberateRemovals(record);
  for (const [relation, counterKey] of Object.entries(counters)) {
    const remoteEntries = store ? readCanonicalRelationshipEntries(store, record, relation) : null;
    audit.push({
      relation,
      loaded: await readLength(record, relation),
      counter: readCounter(record, counterKey),
      linked: readLinkedIds(record, relation),
      remote: remoteEntries === null ? null : remoteEntries.length,
      removed: remoteEntries === null ? null : remoteEntries.filter(entry => removals.has(`${entry.type}:${entry.id}`)).length
    });
  }
  return {
    audit,
    mismatches: audit.filter(row => row.counter > 0 && row.loaded === 0 && !isDeliberatelyEmptied(row)),
    lifecycle: readLifecycle(record)
  };
}

export { EVENT_RELATION_COUNTERS, auditRelationshipCounters, hasLocalRelationshipEdits, readCanonicalRelationshipIds, readLifecycle };
//# sourceMappingURL=relationship-integrity.js.map
